export const en = {
    common: {
        appName: "ITR Agent",
        retry: "Retry",
        loading: "Loading…",
        back: "Back",
        cancel: "Cancel",
        user: "User",
        themeLight: "Light theme",
        themeDark: "Dark theme",
        themeSystem: "System theme",
        langRu: "Russian",
        langEn: "English",
        yes: "yes",
        no: "no",
    },
    nav: {
        section: "Sections",
        dashboard: "Home",
        settings: "Settings",
        strategies: "Strategies",
        orders: "Orders",
        analytics: "Analytics",
        pnlReport: "Positions & PnL",
        journal: "Execution journal",
        admin: "Administration",
        logout: "Log out",
    },
    mode: {
        sandbox: "Sandbox",
        live: "Live",
        scopeTitle: "Data for «{{mode}}» mode",
        sandboxScopeHint: "T-Invest sandbox account and ITR internal model — not your live portfolio.",
        liveScopeHint: "T-Invest live account and ITR orders with mode=live — real money.",
        itrDataHint: "ITR Agent orders and positions for the selected mode only.",
        brokerDataHint: "Snapshot of the real T-Invest account for the selected mode.",
        emptyLiveTrading: "No ITR Agent trades in live mode — PnL and limits are zero.",
    },
    auth: {
        loginTitle: "Sign in",
        registerTitle: "Sign up",
        email: "Email",
        password: "Password",
        name: "Name",
        submitLogin: "Sign in",
        submitRegister: "Create account",
        noAccount: "No account?",
        hasAccount: "Already have an account?",
        linkRegister: "Register",
        linkLogin: "Sign in",
        loggingIn: "Signing in…",
        registering: "Creating account…",
        errorLogin: "Authentication failed",
        errorRegister: "Registration failed",
    },
    dashboard: {
        title: "Home",
        summaryTitle: "Summary ({{mode}})",
        portfolio: "Portfolio",
        pnlDaily: "Daily PnL",
        activeStrategies: "Active strategies",
        openOrders: "Open orders",
        loadSummary: "Loading summary…",
        emptySummary: "Summary unavailable.",
    },
    monitor: {
        healthTitle: "Health indicators",
        apiHealth: "API",
        apiHealthDetail: "WS connections: {{ws}}, uptime {{uptime}}s",
        wsHealth: "WebSocket",
        timelineTitle: "Event timeline",
        timelineEmpty: "No realtime events yet.",
        watchlistHint: "Active strategy tickers: {{tickers}}",
        alertsTitle: "Alerts panel",
        alertsEmpty: "No active alerts.",
        alertFilterAll: "All",
        alertFilterWarning: "Warn",
        alertFilterError: "Error",
        clearFiltered: "Clear filtered",
        clearAll: "Clear all",
        ack: "Ack",
        showHistory: "Show history",
        hideHistory: "Hide history",
        eventDetailsTitle: "Event details",
        noSelectedEvent: "Select an event from the list.",
        noDetails: "No additional details",
        eventType: "Type",
        eventMode: "Mode",
        eventTime: "Time",
        eventId: "Event ID",
        eventPayload: "Payload",
        filterAllTypes: "All event types",
        filterSearch: "Search by event or payload",
        filteredEmpty: "No events match filters.",
        scaleNormalized: "Normalized",
        scaleAbsolute: "Absolute",
        window: {
            "30s": "30s",
            "1m": "1m",
            "5m": "5m",
            all: "All",
        },
        lastPrice: "Last price",
        priceDelta: "Price change",
        signalsErrors: "Signals/Errors",
        absoluteHint: "Absolute mode: raw price values for selected ticker {{ticker}}.",
        normalizedHint: "Normalized mode: all tickers rebased to 100 for fair dynamics comparison.",
        healthTimelineHint: "The timeline shows recent service health: green — available, yellow — degraded, red — down.",
        lastChange: "Last change",
        now: "Now",
        signalAlert: "New strategy signal ({{mode}})",
        signalRejectedAlert: "Signal rejected: {{reason}}",
        wsErrorAlert: "WebSocket connection issue: heartbeat timeout",
        marketDataHealth: "Market data",
        marketDataSource: "Source",
        marketDataHint: "Primary heartbeat: up to 12s is OK; fallback is allowed up to 25s, otherwise trading is blocked.",
        marketDataSourceValue: {
            primary: "Primary",
            fallback: "Fallback",
        },
        pipelineStage: "Pipeline stage",
        reasonCode: "Reason code",
        status: {
            up: "OK",
            degraded: "Degraded",
            down: "Down",
        },
    },
    tokens: {
        title: "API Tokens",
        cardHint: "Token is stored in ITR Agent. «Added» and «rotation» are app policy (90d); T-Invest block appears after Validate. T-Bank portal issue dates are not available via API. Tokens are auto-validated on login.",
        load: "Loading tokens…",
        empty: "No tokens for the selected mode.",
        validate: "Validate",
        save: "Save",
        saving: "Saving…",
        edit: "Edit",
        add: "Add token",
        creating: "Adding…",
        valueLabel: "Token value",
        valuePlaceholder: "Enter API token",
        validationRequired: "Enter token value",
        modeLabel: "Mode",
        editValid: "Token is valid",
        valid: "valid",
        updated: "updated",
        unsavedChanges: "Unsaved changes",
        statusCreated: "Token ({{mode}}) added successfully",
        statusValidated: "Token ({{mode}}) validated successfully",
        statusUpdated: "Token ({{mode}}) updated successfully",
        statusRotatedDowntimeFree: "Secret rotated ({{mode}}): zero-downtime path — new TTL and validation recorded.",
        statusRemoved: "Token ({{mode}}) removed",
        remove: "Remove",
        removeConfirmTitle: "Remove token?",
        removeConfirmDescription: "Token {{id}} will be removed from ITR Agent. The secret in the T-Bank portal is not revoked.",
        removing: "Removing…",
        issuedAt: "Added in ITR Agent",
        expiresAt: "Rotation (app policy, 90d)",
        rotateUntil: "Rotate by",
        lastValidation: "Last validation",
        rotationZdt: "Zero-downtime rotation",
        rotationZdtYes: "supported",
        rotationZdtNo: "not supported",
        validationOutcome: {
            never: "never checked",
            ok: "success",
            failed: "failed",
        },
        tinvestSection: "T-Invest data (GetInfo)",
        tinvestFetchedAt: "Snapshot",
        tinvestAccounts: "Accounts available",
        tinvestTariff: "Tariff",
        tinvestPrem: "Premium",
        tinvestQual: "Qualified investor",
        tinvestQualifiedInstruments: "Qualified instruments",
        boolYes: "yes",
        boolNo: "no",
    },
    settings: {
        title: "Settings",
        profileTitle: "User profile",
        preferencesTitle: "Interface preferences",
        userId: "User ID",
        userName: "Name",
        userEmail: "Email",
        language: "Language",
        theme: "Theme",
        policyTitle: "Strategy/Risk/Execution policies",
        policyHint: "Orchestration, risk-gates and order execution settings for the selected mode.",
        pretradeTitle: "Pre-trade (price band and position transfer)",
        pretradeHint: "Maximum allowed deviation of a LIMIT order price from the last market price. Exceeding it rejects the order with PRETRADE_LIMIT_PRICE_BAND. Also controls transferring a position between strategies while orders are open.",
        pretradeModeNote: "Applies to «{{mode}}» mode. Use the sandbox/live switch above to choose which mode you are editing.",
        pretradePriceBand: "Price band, %",
        pretradePriceBandHint: "Order price is compared to the last Redis price (or position market_price). Default is 3%; volatile names often use 5–8%.",
        pretradePriceBandValidation: "Enter a price band between 0.1% and 50%.",
        pretradeTransferOpenOrders: "Transfer position with open orders",
        pretradeTransferOpenOrdersHint: "By default transfer is blocked while the ticker has orders in open status (including TWAP parents). Allowing it is at your own risk: orders stay on the source strategy.",
        pretradeTransferForbid: "Forbid",
        pretradeTransferAllow: "Allow",
        pretradeSave: "Save pre-trade",
        pretradeSaved: "Saved",
        shadowRolloutTitle: "Shadow rollout (live)",
        shadowRolloutHint: "Automatically route live orders to shadow mode (no real money). Required while prod execution is disabled.",
        shadowRolloutScopeNote: "This setting always applies to Live mode. The sandbox/live toggle above (tokens, risk) does not affect shadow rollout.",
        shadowRolloutEnabled: "Enable shadow rollout for live orders",
        shadowRolloutCanary: "Canary, % (informational)",
        shadowRolloutCanaryHint: "Stored in policy; whitelist/enabled matter more than canary today.",
        shadowRolloutStrategies: "Strategy whitelist (CSV ids)",
        shadowRolloutStrategiesHint: "Empty = all strategies in this mode. Otherwise only listed ids.",
        shadowRolloutAccounts: "Account whitelist (CSV user ids)",
        shadowRolloutAccountsHint: "Empty = current user is not limited by account whitelist.",
        shadowRolloutAlt: "Alternative: enable “Shadow mode” on a specific order on the Orders page.",
        shadowRolloutSave: "Save shadow rollout",
        shadowRolloutSaved: "Saved",
        riskPolicySection: "RiskPolicy (advanced limits)",
        executionPolicySection: "ExecutionPolicy",
        venueRoutingHint: "Venue routing affects T-Invest execution: primary/failover select FIGI. If the instrument is missing on a venue, one retry uses the next (moex/spb). OTC is not supported by the API — do not put it in failover.",
        primaryVenue: "Primary venue",
        venueFailover1: "Failover venue 1",
        venueFailover2: "Failover venue 2",
        venueNone: "— none —",
        venueSwitchCriterion: "Venue switch criterion (ranking vs failover chain)",
        venue: {
            moex: "MOEX (main board)",
            spb: "SPB Exchange",
            otc: "OTC / off-book",
        },
        venueSwitch: {
            min_spread_bps: "Tightest spread (bps) — 2nd in chain",
            max_top_of_book: "Deepest top-of-book — last in chain",
            lowest_latency: "Lowest latency — first in failover",
        },
        portfolioAllocatorSection: "Portfolio allocator",
        portfolioAllocatorHint: "Position — current exposure from open positions (qty × market price). Informational risk view.",
        portfolioAllocatorPretradeHint: "Daily turnover — sum of filled orders since midnight (qty × lot × price). Pretrade enforces strategy/cluster limits against this metric; a new order is blocked when turnover reaches the limit.",
        portfolioLimitLabel: "Limit",
        portfolioPositionLoad: "Position: {{amount}} ({{pct}}%)",
        portfolioTurnoverLoad: "Daily turnover (pretrade): {{amount}} ({{pct}}%)",
        portfolioPretradeBlocks: "blocks new orders",
        portfolioPretradeNearLimit: "low headroom — new buys may be rejected",
        portfolioTurnoverToday: "Total daily turnover for mode: {{amount}} RUB",
        portfolioKpiRejectTitle: "Rejection share (PORTFOLIO_LIMIT_*)",
        portfolioKpiOfMax: "threshold",
        portfolioKpiAttempts: "attempts",
        portfolioKpiRej: "portfolio rejections",
        portfolioKpiRejectOk: "Rejection-rate KPI is met (below threshold).",
        portfolioKpiRejectNo: "Rejection share is not below the DoD threshold.",
        portfolioKpiLiveNetTitle: "Net exposure after fill (live)",
        portfolioKpiLiveOverage: "Post-trade breaches above cap",
        portfolioKpiLiveOk: "No live net exposure overages recorded.",
        portfolioKpiLiveNo: "Post-trade net exposure breach detected (mock: using fill price).",
        portfolioKpiUnavailable: "Portfolio KPIs unavailable (endpoint missing or error).",
        portfolioMaxRejectRatePct: "DoD threshold: max share of portfolio-limit rejections (%)",
        portfolioCapsValidation: "Portfolio caps (budget, net/gross exposure) must be numbers greater than 0.",
        portfolioTotalBudget: "Total portfolio budget",
        portfolioNetExposureCap: "Net exposure cap",
        portfolioGrossExposureCap: "Gross exposure cap",
        portfolioUsed: "Used",
        portfolioUtilization: "Utilization",
        portfolioStrategies: "Strategy limits",
        portfolioClusters: "Sector/cluster limits",
        portfolioNoRows: "No configured limits.",
        maxPositionNotional: "Max position notional",
        maxSimultaneousPositions: "Max simultaneous positions",
        allowedTickers: "Allowed tickers (comma-separated)",
        blockedTickers: "Blocked tickers (comma-separated)",
        maxExposurePerTicker: "Max exposure per ticker",
        maxOrdersPerTickerPerHour: "Max orders per ticker per hour",
        slippageThresholdBps: "Slippage threshold (bps)",
        commissionRatePct: "Broker commission, % (per side)",
        volatilityGuardEnabled: "Enable volatility guard",
        fieldHint: {
            totalBudgetNotional: "Mode budget / turnover ceiling (currency units). An order that would push daily turnover over the cap is rejected (PORTFOLIO_LIMIT_TOTAL_BUDGET).",
            netExposureCapNotional: "Max net exposure |sum qty×price| after the order. Longs and shorts partially offset. Often the tightest cap for long-only books.",
            grossExposureCapNotional: "Max gross exposure: sum of |qty×price| without netting. Grows faster than net when both longs and shorts exist.",
            maxPositionNotional: "Max notional of a single position (≈ qty × lot × price). An order that would exceed the limit is rejected by pretrade.",
            maxSimultaneousPositions: "How many different open positions may be held at once. Lower value means less capital spread and fewer parallel losses.",
            allowedTickers: "Mode-level whitelist (comma-separated). Empty = no list restriction. Applies on top of StrategyPolicy: a strategy-allowed ticker can still be blocked here.",
            blockedTickers: "Mode blacklist: blocks both BUY and SELL. To pause buys only, use Buy lock on the strategy ticker card.",
            maxExposurePerTicker: "Max exposure (currency) per ticker in total. Limits concentration risk in one name.",
            maxOrdersPerTickerPerHour: "Anti-churn: max orders per ticker per hour. Reduces BUY↔SELL series and fee burn on noise.",
            commissionRatePct: "Accounting fee per trade side. Affects breakeven, PnL, and “block MA sell below average”. Should match the broker tariff (often ~0.3).",
            slippageThresholdBps: "Slippage threshold in basis points (1 bps = 0.01%). Above it, risk gates / execution-quality checks may fire.",
            volatilityGuardEnabled: "Extra protection in high-volatility periods: blocks or tightens entries more often to avoid noisy trades.",
        },
        routingProfile: "Routing profile",
        limitOffsetBps: "Limit price offset (bps)",
        executionAdaptiveHint: "Adaptive offset, child-order slicing, and fallback on mild market-data degradation (stale primary while fallback is still within tolerance; not the full trading block). In mock, affects slippage/latency/partial fill modeling.",
        adaptiveOffsetEnabled: "Adaptive extra offset / bps when liquidity is poor",
        adaptiveOffsetMaxExtraBps: "Max extra bps on degradation (cap)",
        maxChildOrderSlices: "Max child order waves (slicing)",
        minChildOrderQtyRatio: "Min parent qty ratio per wave (0…1)",
        liquidityDegradedFallback: "When liquidity drops (mild, before hard block)",
        liquidityFallback: {
            widen_offset: "Widen offset (extra slippage)",
            pause_slicing: "Pause slicing (single wave)",
            widen_and_slow: "Widen and slow (higher latency)",
        },
        retryCount: "Retry count",
        retryBackoffMs: "Retry backoff (ms)",
        partialFillTimeoutSec: "Partial fill timeout (sec)",
        emergencyExitMode: "Emergency exit mode",
        cancelReplaceEnabled: "Enable cancel/replace",
        savePolicy: "Save policies",
    },
    risk: {
        title: "Risk panel",
        description: "Limits for {{mode}} mode",
        load: "Loading risk settings…",
        maxDailyLoss: "Max daily loss",
        maxWeeklyLoss: "Max weekly loss",
        maxTradesPerDay: "Max trades per day",
        maxNotionalPerDay: "Max daily notional",
        fieldHint: {
            maxDailyLoss: "Cap on realized loss for the calendar day (UTC). When reached, BUY/add-on orders are blocked; reduce-only SELL only in profit (price ≥ breakeven with fees). Stop-loss is not exempt.",
            maxWeeklyLoss: "Same for the week. Can trigger even if the daily limit is not yet exhausted.",
            maxTradesPerDay: "Hard trade counter per day. Main lever against churn and fee accumulation. Reduce-only SELL is not blocked.",
            maxNotionalPerDay: "Total daily turnover in currency (order volume), not trade count. Caps trading scale; reduce-only SELL remains allowed when the limit is hit.",
            killSwitchScope: "Emergency stop scope: selected strategy only, whole mode (sandbox/live), or whole account.",
            killSwitchStrategyId: "Strategy ID for “strategy” scope. Without a correct ID, strategy-scoped kill switch will not behave as expected.",
        },
        killSwitchEnabled: "Kill Switch is on: trading is stopped",
        killSwitchDisabled: "Kill Switch is off: trading is allowed",
        killSwitchOnButton: "Enable Kill Switch",
        killSwitchOffButton: "Disable Kill Switch",
        killSwitchControlTitle: "Kill Switch",
        killSwitchControlHint: "Emergency stop/allow trading for the selected mode.",
        killSwitchScope: "Kill Switch scope",
        killSwitchScopeStrategy: "Strategy",
        killSwitchScopeMode: "Mode",
        killSwitchScopeAccount: "Account",
        killSwitchStrategyId: "Strategy ID for strategy scope",
        killSwitchScenario: "Enabling kill switch in live mode stops new strategy signals, blocks new and working orders, and deactivates strategies until you turn it off. You will be asked to confirm. In sandbox, changes apply immediately.",
        killSwitchRunbook: "Runbook: after enabling Kill Switch, verify active strategies, review/cancel working orders, and confirm API/WS stability before unblocking.",
        switchOn: "On",
        switchOff: "Off",
        save: "Save limits",
        validation: "Check limits: only non-negative numbers are allowed, trades count must be an integer.",
        updated: "Risk limits updated",
        killSwitchOn: "Kill Switch enabled",
        killSwitchOff: "Kill Switch disabled",
    },
    strategies: {
        title: "Strategies",
        backDashboard: "Back to dashboard",
        createSection: "New strategy",
        create: "Create",
        namePlaceholder: "Name",
        defaultName: "New strategy",
        load: "Loading strategies…",
        empty: "No strategies for the selected mode.",
        emptyHint: "Create your first strategy or open an existing one.",
        listCount: "Strategies: {{count}}",
        createOpen: "Create strategy",
        createCollapse: "Hide form",
        cardActions: "Strategy actions",
        editorTabs: "Strategy sections",
        tabOverview: "Overview",
        tabParams: "Params",
        tabPolicy: "Tickers & policy",
        tabVersions: "Versions",
        active: "Active",
        inactive: "Inactive",
        accordionTickersEmpty: "No tickers configured",
        accordionOpenMark: "open",
        accordionExpandHint: "Params and policy",
        autoTradingTitle: "Auto-trading (sandbox)",
        autoTradingRunning: "Runner is active — orders are placed on MA Cross signals",
        autoTradingNoSignals: "No signals yet (bar history required in Redis)",
        autoTradingLastSignal: "Last signal",
        autoTradingLastOrder: "Last order",
        autoTradingBarCount: "bars",
        exitHintsTitle: "Position exit",
        exitPnlNow: "Strategy position PnL: {{pct}}%",
        exitTakeProfitRemaining: "To take-profit ({{target}}%): {{pct}}% remaining",
        exitTakeProfitReached: "Take-profit {{target}}% reached — protective SELL expected",
        exitStopLossDistance: "To stop-loss (−{{target}}%): {{pct}}% headroom",
        exitStopLossReached: "Stop-loss −{{target}}% reached — SELL expected",
        exitNoPosition: "No open position for this ticker on this strategy",
        exitMaSellNote: "MA SELL only on bearish fast/slow crossover, not on price moves alone",
        transferPosition: "Move position",
        transferSuccess: "{{ticker}} moved to “{{strategy}}”.",
        lastSkipReason: "Last skipped signal",
        skipReason: {
            ma_buy_already_long: "BUY skipped: position already open (long-only)",
            ma_sell_no_position: "SELL skipped: no position to close",
            ma_sell_below_avg: "MA SELL skipped: price below average (block_ma_sell_in_loss)",
            ma_sell_below_min_pnl: "MA SELL skipped: profit below ma_sell_min_pnl_pct threshold",
            pending_position_rules: "Pending signal abandoned: position rules",
            pretrade: "Pretrade rejected order: {{code}}",
        },
        modeLabel: "Mode",
        activate: "Activate",
        deactivate: "Deactivate",
        clone: "Clone",
        remove: "Remove",
        statusCreated: "Strategy created",
        statusDeactivated: "Strategy deactivated",
        statusActivated: "Strategy activated",
        statusCloned: "Strategy cloned to {{mode}}",
        statusRemoved: "Strategy removed",
        statusUpdated: "Strategy parameters saved",
        statusRenamed: "Strategy name saved",
        nameRequired: "Enter a strategy name.",
        statusRollback: "Strategy configuration rolled back",
        policySaved: "Strategy policy saved",
        saveName: "Save name",
        saveParams: "Save parameters",
        versionsTitle: "Configuration versions",
        loadVersions: "Load versions",
        versionsEmpty: "Version history is not loaded yet.",
        rollback: "Rollback",
        policyTitle: "StrategyPolicy",
        policyPriority: "Priority",
        policyConflict: "Conflict policy",
        policyAllowedTickers: "Strategy tickers",
        tickerAdd: "Add",
        tickerRemove: "Remove",
        tickerLockStopLoss: "Stop-loss lock",
        tickerLockBuy: "Buy lock",
        tickerDuplicate: "Ticker {{ticker}} is already in the list.",
        tickerInOtherStrategy: "Ticker {{ticker}} is already in strategy «{{name}}». One ticker per strategy in the same mode.",
        tickerOpenInOtherStrategy: "Ticker {{ticker}} already has an open position in strategy «{{name}}». Close it or remove the ticker there first.",
        policyExecutionProfile: "Execution profile",
        policyPreview: "Current rule",
        savePolicy: "Save policy",
        policyGuardsSection: "Execution limits and time window",
        policyExpectedSlippageBps: "Expected slippage (bps)",
        policyMaxSpreadBps: "Max spread (bps)",
        policyMinLiquidityNotional: "Min liquidity (notional)",
        policySessionStart: "Session start (HH:mm)",
        policySessionEnd: "Session end (HH:mm)",
        policyTimezone: "Timezone (IANA)",
        policyOnlyRth: "Regular trading hours only (RTH)",
        policyOnlyRthYes: "Yes",
        policyOnlyRthNo: "No",
        policyNewsBlackoutMinutes: "News blackout (±min, 0=off)",
        policyGuardsValidation: "Check numeric limits: slippage/spread 0..500, liquidity ≥0, blackout 0..240 integer.",
        policyScheduleValidation: "Check time window: HH:mm and non-empty timezone.",
        experimentTitle: "A/B experiments (legacy)",
        experimentHint: "Legacy concurrent split; prefer What-if when tickers are exclusive.",
        whatIfTitle: "What-if experiments",
        whatIfHint: "Bound to one strategy: at period_from fact and hypothesis start from the same positions (FIFO from orders before start). Then hypothesis simulates MA/SL/TP on bars — report only, no PostOrder.",
        whatIfParamsHint: "Params below are copied from the selected strategy — change only what you test. Sim: MA, SL/TP, MA SELL rules, loss_avg (with bottom), MA-SELL cooldown, stop-loss lock.",
        whatIfName: "Name",
        whatIfHypothesis: "Hypothesis",
        whatIfStrategy: "Strategy",
        whatIfCreate: "Create what-if",
        whatIfEmpty: "No what-if experiments yet.",
        whatIfRunReport: "Build report",
        whatIfFact: "Fact",
        whatIfHyp: "Hypothesis",
        whatIfRec: "Recommendation",
        whatIfByTicker: "Breakdown by ticker",
        whatIfTicker: "Ticker",
        whatIfRebuildReport: "Rebuild report",
        whatIfSignalsFact: "Signals (fact)",
        whatIfSignalsHyp: "Signals (hypothesis)",
        whatIfJournalFact: "Trade journal — fact",
        whatIfJournalHyp: "Trade journal — hypothesis",
        whatIfTradesEmpty: "No trades in the period.",
        whatIfColTime: "Time",
        whatIfColSide: "Side",
        whatIfColSignal: "Signal",
        whatIfColPrice: "Price",
        whatIfColQty: "Lots",
        whatIfOpening: "Start (positions at period start)",
        whatIfOpeningFlat: "flat",
        whatIfEdit: "Edit",
        whatIfSave: "Save",
        whatIfSaveAndRebuild: "Save and rebuild",
        whatIfCancelEdit: "Cancel",
        whatIfDuplicate: "Duplicate",
        whatIfDelete: "Delete",
        whatIfDeleteTitle: "Delete what-if experiment?",
        whatIfDeleteDesc: "The experiment and stored report will be permanently removed.",
        whatIfUpdated: "What-if experiment updated",
        whatIfDeleted: "What-if experiment deleted",
        whatIfDuplicated: "What-if experiment duplicated",
        whatIfFilterTicker: "Ticker",
        whatIfFilterAll: "All",
        whatIfParamsDiff: "Param changes (baseline → hypothesis)",
        whatIfModelNotes: "Model notes",
        whatIfRecValue: {
            hypothesis_better: "hypothesis better",
            fact_better: "fact better",
            tie: "tie",
            insufficient_data: "insufficient data",
        },
        whatIfNeedStrategy: "Select a strategy",
        whatIfDefaultHypothesis: "Alternate MA/SL/TP parameters",
        whatIfCreated: "What-if experiment created",
        whatIfReportReady: "What-if report ready",
        experimentName: "Experiment name",
        experimentHypothesis: "Hypothesis",
        experimentStrategyA: "Strategy A (id)",
        experimentStrategyB: "Strategy B (id)",
        experimentSplitA: "Traffic to A, %",
        experimentMinTrades: "Min trades per branch",
        experimentMaxDd: "Max drawdown limit (ratio, 0..1)",
        experimentMinSlipImprove: "Min slippage improvement (ratio, 0..1)",
        experimentCreate: "Create experiment",
        experimentCreated: "A/B experiment created",
        experimentUpdated: "Experiment status updated: {{status}}",
        experimentStart: "Start",
        experimentStop: "Stop",
        experimentEmpty: "No experiments for this mode yet.",
        experimentKpiHint: "Branch A/B KPI in the mock are derived from orders in the experiment window (filled/shadow, rejects, slippage).",
        experimentBranchA: "Branch A",
        experimentBranchB: "Branch B",
        experimentKpiPnl: "PnL (mock)",
        experimentKpiMaxDd: "Max drawdown",
        experimentKpiAvgSlippage: "Avg slippage, bps",
        experimentKpiRejectRate: "Reject rate",
        experimentKpiStability: "Stability",
        experimentKpiTrades: "Trades",
        experimentWinnerBlock: "Auto rule from KPI",
        experimentWinnerReady: "Stop criteria are met — the recommendation is active.",
        experimentWinnerNotReady: "Criteria not met — recommendation stays none/tie until enough data.",
        experimentWinnerSuggested: "Suggested branch: {{branch}}",
        experimentWinnerReasons: "Rationale",
        experimentSuggestedTie: "tie",
        experimentSuggestedNone: "no recommendation",
        experimentPromoteA: "Promote A",
        experimentPromoteB: "Promote B",
        experimentRollback: "Rollback",
        experimentOutcomeTitlePromoteA: "Confirm promote branch A",
        experimentOutcomeTitlePromoteB: "Confirm promote branch B",
        experimentOutcomeTitleRollback: "Confirm rollback",
        experimentOutcomeDescPromoteA: "The experiment will be marked completed with branch A as the promoted winner.",
        experimentOutcomeDescPromoteB: "The experiment will be marked completed with branch B as the promoted winner.",
        experimentOutcomeDescRollback: "Rollback will be recorded without promote; experiment status becomes stopped.",
        experimentOutcomeConfirm: "Confirm",
        experimentOutcomeSaved: "A/B experiment outcome saved.",
        experimentDecisionPromoted: "Outcome: promote {{branch}}",
        experimentDecisionRolledBack: "Outcome: rollback (no promote)",
        experimentDecisionLogTitle: "Decision log",
        experimentLogEventCreated: "created / journal bootstrap",
        experimentLogEventStatus: "status change",
        experimentLogEventOutcome: "outcome committed",
        experimentLogFingerprint: "Criteria fingerprint (replay)",
        experimentValidation: "Check split/criteria: numeric values, split must be in (0;100).",
        policyPriorityValidation: "Priority must be an integer in range 1..100.",
        validationNumeric: "All params must be numbers.",
        validationInteger: "fast_ma, slow_ma, ma_reentry_cooldown_min, loss_avg_bottom_bars and lot must be integers.",
        validationFastSlow: "fast_ma must be less than slow_ma.",
        validationLossAvgVsStop: "Each loss-averaging threshold (pnl_pct) must be > 0 and below stop_loss.",
        validationLossAvgStepsOrder: "Loss-averaging ladder thresholds must be non-decreasing (each ≥ previous).",
        validationLossAvgLotsMult: "Step lots_mult must be greater than 0 and at most 20.",
        validationLossAvgStepsMax: "At most 10 loss-averaging steps.",
        validationLossAvgBottomBars: "Bottom window (loss_avg_bottom_bars): 0 or 2..30 (1 is invalid).",
        validationRange: "Parameter {{key}} is out of range {{min}}..{{max}}.",
        validationBlockMaSell: "Block MA sell in loss must be 0 or 1.",
        validationLockStopLossOnBuy: "Lock stop loss on buy must be 0 or 1.",
        lossAvgStepsEmpty: "Ladder is empty — loss averaging is off. Add steps: threshold % × lot multiplier.",
        lossAvgStepAdd: "Add step",
        lossAvgStepRemove: "Remove",
        paramSection: {
            signal: "MA Cross signal",
            exits: "Hard exits",
            maExit: "MA exit rules",
            lossAvg: "Loss averaging",
            order: "Order size",
        },
        param: {
            fast_ma: "Fast MA",
            slow_ma: "Slow MA",
            stop_loss: "Stop loss (%)",
            take_profit: "Take profit (%)",
            ma_sell_min_pnl_pct: "Min profit for MA SELL (%)",
            ma_reentry_cooldown_min: "Cooldown after MA SELL (min)",
            loss_avg_pct: "Loss average (%)",
            loss_avg_max: "Max loss averages",
            loss_avg_steps: "Loss-average ladder",
            loss_avg_step_pct: "Loss threshold % (step {{index}})",
            loss_avg_step_mult: "Lot multiplier",
            loss_avg_bottom_bars: "Bottom: bar window",
            lot: "Lot",
            block_ma_sell_in_loss: "Block MA SELL below average cost",
            lock_stop_loss_on_buy: "Lock stop loss on buy",
        },
        paramHint: {
            fast_ma: "Fast moving-average period. Smaller = more sensitive to noise and more signals; larger = fewer entries.",
            slow_ma: "Slow moving-average period. Must be greater than Fast MA. Together they set BUY/SELL crossover frequency.",
            stop_loss: "Forced SELL when position loss (incl. sell fee) hits this % from average entry. Lower % cuts losses faster but stops out on noise more often.",
            take_profit: "Forced SELL when profit hits this %. Keep it above round-trip fees (~0.6% at 0.3% per side), or price gains turn into net losses after commissions.",
            ma_sell_min_pnl_pct: "MA crossover SELL is allowed only when net PnL is at least this %. 0 disables the extra floor (block_ma_sell_in_loss still applies). Stop loss and take profit are unchanged.",
            ma_reentry_cooldown_min: "Pause in minutes after an MA Cross SELL during which the next auto-BUY for the same ticker is blocked. 0 disables the cooldown.",
            loss_avg_pct: "Buy another strategy lot when position loss (incl. fees) reaches this %. 0 disables. Must be below stop_loss. Next buy only after PnL recovers above the threshold then falls again (latch), until the max count is reached.",
            loss_avg_max: "How many average-down buys per open-position cycle. Counter resets when the position is fully closed. 0 disables. Funds are gated by pretrade budgets; price must be below the last sell (or average entry if never sold).",
            loss_avg_steps: "Average-down ladder: each step is a loss threshold % and lots_mult vs base lot. Index = loss_avg_count. Empty = off. Example: 4×1, 7×1, 10×2. Thresholds non-decreasing and below stop_loss.",
            loss_avg_bottom_bars: "After the loss threshold, wait for a local bottom over the last N bars: window min is not on the last bar and the last bar is above the previous one. 0 = buy at threshold; typically 3–5. Value 1 is invalid.",
            lot: "Lots per auto order. Scales notional, PnL and fees per trade; does not change signal frequency. The allowed range comes from lot settings (Settings → Lot settings): {{min}}–{{max}}.",
            block_ma_sell_in_loss: "Skip MA-crossover SELL when price is below breakeven (average + fees). Stop loss and take profit still apply.",
            lock_stop_loss_on_buy: "On buy, automatically lock stop loss for the ticker. Manual sell and take profit stay available; unlock via the Orders padlock.",
            exitRules: "Stop loss / take profit exit by % from position average. MA block skips bearish crossover when price is below average.",
        },
        policyFieldHint: {
            priority: "Strategy priority on conflicting signals (1–100). Higher number wins over other active strategies.",
            conflict_policy: "What to do on conflict with another strategy: skip, reduce size, hedge, or reverse.",
            allowed_tickers: "Strategy ticker cards. Per ticker: lock stop-loss after buy; buy lock rejects new BUY (manual and auto). Full ticker blacklist lives in mode RiskPolicy (Settings). Empty = no strategy-level list limit (mode RiskPolicy still applies).",
            execution_profile: "Execution profile (passive / balanced / aggressive): affects limit aggressiveness and slippage expectations.",
            expected_slippage_bps: "Expected slippage in bps. Used in quality gates: too large a deviation may skip the signal/order.",
            max_spread_bps: "Max allowed book spread in bps. Wider spread means higher cost; above the cap entry is blocked (needs GetOrderBook).",
            min_liquidity_notional: "Min liquidity notional: with a book — top-of-book floor (below → block); also the large-order threshold for auto-TWAP.",
            session_start: "Strategy session start (HH:mm) in the given timezone. Together with session end defines the policy calendar.",
            session_end: "Strategy session end (HH:mm). Outside the window calendar-based auto-trading is limited (unless the broker already confirms trading).",
            timezone: "IANA timezone for the strategy window (usually Europe/Moscow for MOEX). Not the agent window under Notifications & session.",
            only_rth: "Regular trading hours only: strategy follows the main session window without extended policy intervals.",
            news_blackout_minutes: "Pause ±N minutes around news markers (0 = off). Reduces trading during sharp volatility spikes.",
        },
        preset: {
            conservative: "Preset: Conservative",
            balanced: "Preset: Balanced",
            aggressive: "Preset: Aggressive",
        },
        conflictPolicy: {
            skip: "Skip",
            reduce: "Reduce",
            hedge: "Hedge",
            reverse: "Reverse",
        },
        executionProfile: {
            passive: "Passive",
            balanced: "Balanced",
            aggressive: "Aggressive",
            twap: "TWAP",
            vwap: "VWAP",
            pov: "POV",
        },
    },
    market: {
        scheduleTitle: "Exchange schedule",
        scheduleAccordionHint: "Click to expand the schedule",
        scheduleLoading: "Loading schedule…",
        scheduleExchange: "{{exchange}} · {{timezone}}",
        scheduleSourceTinvest: "T-Invest API",
        scheduleSourceFallback: "estimate",
        tradableNow: "tradable now",
        notTradableNow: "not tradable now",
        tradabilityUnknown: "status unknown",
        marketOrders: "Market orders: {{available}}",
        limitOrders: "Limit orders: {{available}}",
        todaySession: "Today",
        exchangeTodaySession: "Exchange today",
        exchangeTodayHint: "Official MOEX hours from T-Invest; may differ from the agent session in Settings.",
        agentSessionTitle: "Agent session window",
        agentSessionActive: "Currently inside the configured agent window.",
        agentSessionInactive: "Currently outside the configured agent window.",
        agentSessionDisabled: "Session window limit is off (only RTH disabled).",
        agentSessionHint: "Configured in Settings → Notifications and session. Strategy pretrade uses per-strategy schedule on the Strategies page.",
        nonTradingDay: "Non-trading day",
        nonTradingDayShort: "Holiday",
        tradingDay: "Trading",
        noSessionsToday: "No sessions defined",
        colDate: "Date",
        colDayType: "Day type",
        colSessions: "Sessions (MSK)",
        blockedBecause: "Cannot trade this ticker because:",
        nextTradable: "Next exchange window: {{when}}",
        nextTradableHint: "Exact instrument availability still depends on T-Invest status at session open.",
    },
    checks: {
        title: "Checks",
        hint: "Verify whether a ticker can be traded now via T-Invest API in the selected mode (limit and market orders separately).",
        tickerPlaceholder: "Ticker",
        checkButton: "Check",
        checking: "Checking…",
        enterTicker: "Enter a ticker",
        notFound: "Instrument not found or unavailable",
        instrumentTitle: "Instrument",
        lotSize: "Lot size: {{lot}}",
        figi: "FIGI: {{figi}}",
        source: "Lot data source: {{source}}",
        sourceTinvest: "T-Invest API",
        sourceCache: "cache",
        sourceDefault: "default",
        orderTypeLimit: "Limit order",
        orderTypeMarket: "Market order",
        apiTrade: "API trading: {{available}}",
        forQualInvestor: "Qualified investor required: {{value}}",
        userQualStatus: "Account qual status: {{value}}",
        tradabilitySourceTinvest: "Status: T-Invest API",
        tradabilitySourceFallback: "Status: schedule estimate",
        tradabilitySourceUnavailable: "Status: unavailable",
        tradabilitySourceCacheMiss: "Status: waiting for worker-market cache",
    },
    readiness: {
        title: "Trading readiness",
        accordionHint: "Click to expand the checklist",
        hint: "Pre-flight checklist: account, token, workers, kill-switch, market data, and live mode (shadow only for now).",
        refresh: "Refresh",
        loading: "Checking readiness…",
        checkedAt: "Checked: {{when}}",
        activeStrategies: "Active strategies: {{count}}",
        workersTitle: "Workers",
        workerAlive: "online",
        workerDead: "offline",
        prodDisabledBadge: "no real money",
        overall: {
            ready: "Ready",
            ready_shadow: "Ready for live shadow",
            not_ready: "Not ready",
        },
        status: {
            ok: "OK",
            warn: "Warn",
            fail: "Blocker",
            info: "Info",
        },
    },
    orders: {
        title: "Orders",
        backDashboard: "Back to dashboard",
        load: "Loading orders…",
        empty: "No orders for the selected mode.",
        colId: "ID",
        colTicker: "Ticker",
        colSide: "Side",
        colSignalSource: "Signal",
        signalSource: {
            ma_cross: "MA Cross",
            stop_loss: "Stop loss",
            take_profit: "Take profit",
            loss_avg: "Loss average",
            manual: "Manual",
            broker_import: "Broker import",
        },
        colQty: "Qty",
        colPrice: "Price",
        colAmount: "Amount",
        colFee: "Fee",
        feeSourceActual: "actual",
        feeSourceEstimated: "est.",
        colStatus: "Status",
        colOperationAt: "Operation date",
        colReason: "Reason code",
        colTaxonomy: "Reject class",
        colQueue: "Queue #",
        colTtf: "TTF, ms",
        colAmend: "Cancel / replace",
        colActions: "Actions",
        actionCancel: "Cancel",
        actionReplace: "Replace price",
        twapSlices: "{{count}} slices",
        twapExpand: "Show slices",
        twapCollapse: "Collapse slices",
        twapSliceLabel: "slice {{index}}",
        rejectTaxonomy: {
            risk: "Risk",
            portfolio: "Portfolio",
            market_data: "Market data",
            venue: "Venue / router",
            validation: "Validation",
            runtime: "Runtime / kill-switch",
            idempotency: "Idempotency",
            other: "Other",
        },
        amendCancel: "cancel",
        amendReplace: "replace",
        amendOk: "ok",
        amendFail: "fail",
        colMode: "Mode",
        colExecutionProfile: "Execution profile",
        colVenue: "Venue",
        createTitle: "New order",
        createOpenAria: "Open new order form",
        createCloseAria: "Close new order form",
        createExecutionProfile: "Execution profile (order)",
        createShadowMode: "Shadow mode (virtual fill, no external order send)",
        executionProfileInherit: "From strategy policy",
        createStrategy: "Strategy ID",
        createTicker: "Ticker",
        createQty: "Quantity",
        createQtyLots: "Lots",
        createPrice: "Price per share",
        lotHint: "1 lot = {{lot}} shares",
        orderAmountPreview: "≈ {{amount}} ({{shares}} shares)",
        create: "Submit order",
        creating: "Submitting…",
        created: "Order created: {{id}}",
        createValidation: "Fill order form with valid values",
        noStrategies: "No strategies for this mode. Create one on the Strategies page.",
        dailyLimitsTitle: "Daily limits",
        draftExceedsNotional: "Order amount ({{amount}}) exceeds remaining notional headroom ({{headroom}}).",
        filtersTitle: "Order filters",
        filterStrategy: "Filter by strategy",
        filterTicker: "Filter by ticker",
        filterStatusAll: "All statuses",
        cancelAllOpen: "Cancel all open",
        resetFilters: "Reset filters",
        cancelledCount: "Open orders cancelled: {{count}}",
        orderCancelled: "Order {{id}} cancelled.",
        orderReplaced: "Order {{id}}: replace at {{price}}.",
        positionsTitle: "Positions and strategies",
        positionsCardsHint: "Cards for active strategy tickers: buy, chart, PnL, and exits. Full ITR/broker reconciliation is in Positions & PnL.",
        activeStrategyTickersTitle: "Active strategy tickers",
        activeStrategyTickersEmpty: "No active strategies.",
        activeStrategyTickersMissingPolicy: "No tickers in strategy policy",
        activeStrategyTickersHint: "Quick buy: MARKET BUY using the strategy lot and the current price (from position or quote). Live mode asks for confirmation.",
        activeStrategyColTicker: "Ticker",
        activeStrategyColStrategy: "Strategy",
        activeStrategyColLot: "Lots",
        activeStrategyColLotSize: "Lot size",
        activeStrategyColShares: "Shares",
        activeStrategyColPrice: "Price",
        activeStrategyHasPosition: "In position: {{qty}}",
        activeStrategyBuy: "Buy",
        activeStrategyBuyAria: "Buy {{ticker}}",
        activeStrategyBuying: "Buying…",
        dayChartOpenAria: "Intraday price chart: {{ticker}}",
        dayChartOpenTitle: "Intraday chart",
        dayChartTitle: "{{ticker}} — intraday",
        dayChartSubtitle: "Minute candlesticks for today and filled BUY/SELL. Empty and inactive no-move stretches are compressed.",
        dayChartSubtitleStrategy: "Strategy {{strategy}}. Minute candlesticks for today and filled BUY/SELL. Empty and inactive no-move stretches are compressed.",
        dayChartMaHint: "MA{{fast}}/MA{{slow}} over candles. MA crosses are counted in the footer (not drawn, to keep the chart readable). Trades are B/S markers.",
        dayChartMaFast: "MA{{period}} fast",
        dayChartMaSlow: "MA{{period}} slow",
        dayChartMaCross: "MA cross",
        dayChartMaCrossUp: "MA cross ↑",
        dayChartMaCrossDown: "MA cross ↓",
        dayChartMaCrossCount: "MA crosses: {{count}}",
        dayChartClose: "Close",
        dayChartLoading: "Loading chart…",
        dayChartEmpty: "No price data for today yet. Bars appear after the market worker accumulates history.",
        dayChartPrice: "Price",
        dayChartCandles: "Candles",
        dayChartVolume: "Volume",
        dayChartVolumeHint: "Volume ≈ bar range (worker has no trade volume)",
        dayChartTimeframe: "Timeframe",
        dayChartTimeframeMin: "m",
        dayChartOhlcOpen: "O",
        dayChartOhlcHigh: "H",
        dayChartOhlcLow: "L",
        dayChartOhlcClose: "C",
        dayChartBuy: "Buy",
        dayChartSell: "Sell",
        dayChartTradesCount: "Trades: {{count}}",
        dayChartOutOfRange: "outside quote range: {{count}}",
        dayChartShares: "sh.",
        activeStrategyBuyConfirmTitle: "Buy {{ticker}}?",
        activeStrategyBuyConfirmDescription: "A MARKET BUY order will be created: {{lots}} lot(s) at ~{{price}} (strategy {{strategy}}). Size comes from strategy params. In live mode it is sent as a cash purchase (no forced margin confirm).",
        activeStrategyBuyCreated: "Bought {{ticker}}: order {{id}}.",
        activeStrategyBuyNoPrice: "No live price for {{ticker}}. Wait for a quote or set the price in “New order”.",
        positionsEmpty: "No positions.",
        positionsPnlTotal: "Total PnL",
        posTicker: "Ticker",
        posQty: "Qty",
        posAvgPrice: "Average price",
        posMarketPrice: "Live price",
        posBreakeven: "Breakeven",
        posPnl: "PnL",
        posPnlPct: "Current %",
        posPnlPctHint: "Includes sell commission (same basis as PnL and Breakeven), not raw price delta.",
        posStopLossPct: "Stop loss %",
        posTakeProfitPct: "Take profit %",
        posLossAvgPct: "Loss average %",
        posActions: "Actions",
        sellMenuTitle: "Sell",
        sellMenuAria: "Sell {{ticker}}",
        sellOneLot: "Sell 1 lot",
        sellAllLots: "Sell all",
        sellLockTitle: "Lock stop loss",
        sellUnlockTitle: "Unlock stop loss",
        sellLockAria: "Lock stop loss for {{ticker}}",
        sellUnlockAria: "Unlock stop loss for {{ticker}}",
        sellLockEnabled: "Stop loss for {{ticker}} is locked. Manual sell and take profit remain allowed.",
        sellLockDisabled: "Stop loss for {{ticker}} unlocked.",
        sellLockedHint: "Stop loss for {{ticker}} is locked. Unlock to enable the stop.",
        blockAllSellsTitle: "Block all sells",
        unblockAllSellsTitle: "Unblock all sells",
        blockAllSellsAria: "Block all sells for {{ticker}}",
        unblockAllSellsAria: "Unblock all sells for {{ticker}}",
        blockAllSellsEnabled: "All sells for {{ticker}} are blocked: manual, take profit, and auto SELL are rejected.",
        blockAllSellsDisabled: "Sells for {{ticker}} are allowed again.",
        allSellsBlockedBadge: "Sells blocked",
        allSellsBlockedHint: "All sells for {{ticker}} are blocked. Unblock on the position card.",
        buyLockTitle: "Lock buys",
        buyUnlockTitle: "Allow buys",
        buyLockAria: "Lock buys for {{ticker}}",
        buyUnlockAria: "Allow buys for {{ticker}}",
        buyLockEnabled: "Buys for {{ticker}} are locked (strategy lock_buy). Auto and manual BUY are rejected.",
        buyLockDisabled: "Buys for {{ticker}} are allowed again.",
        buyLockedHint: "Buys for {{ticker}} are locked. Unlock on the card or in strategy policy.",
        transferTitle: "Move to another strategy",
        transferAria: "Move {{ticker}} position to another strategy",
        transferDialogTitle: "Move {{ticker}} position",
        transferDialogDescription: "Quantity and average price stay the same. The target strategy then manages exits (new SL/TP/averaging). Runtime state (loss_avg, cooldown) is reset. Order history is unchanged.",
        transferSelectLabel: "Target strategy",
        transferConfirm: "Move",
        transferSuccess: "{{ticker}} moved to “{{strategy}}”.",
        transferNoTargets: "No other strategy in this mode. Create one, then try again.",
        transferBusy: "Moving…",
        sellLotConfirmTitle: "Sell {{ticker}}?",
        sellLotConfirmDescription: "A MARKET SELL order will be created: {{lots}} lot(s) at ~{{price}} (strategy {{strategy}}). By confirming you accept an order that may lead to an uncovered position at the broker (confirmMarginTrade).",
        sellLotBusy: "Selling…",
        sellLotCreated: "Sold {{ticker}}: order {{id}}.",
        sellLotNoLots: "Position {{ticker}} has no full lot to sell (qty {{qty}}, lot {{lot}}).",
        sellLotNoStrategy: "Position {{ticker}} has no strategy.",
        status: {
            open: "Open",
            filled: "Filled",
            shadow_filled: "Shadow filled",
            rejected: "Rejected",
            cancelled: "Cancelled",
        },
    },
    analytics: {
        title: "Analytics",
        backDashboard: "Back to dashboard",
        load: "Loading analytics…",
        empty: "Analytics unavailable.",
        winRate: "Win rate",
        maxDd: "Max drawdown",
        sharpe: "Sharpe",
        sortino: "Sortino",
        calmar: "Calmar",
        cvar95: "CVaR 95%",
        regimeTitle: "Regime-based performance",
        regimeHint: "Period share and return by mock market regime (stress — deep drawdown; range — low volatility).",
        regimeShare: "Period share",
        regimeReturn: "Return, %",
        regimeSharpe: "Sharpe",
        regimeColumn: "Regime",
        regime: {
            range_low_vol: "Range, low volatility",
            trend_up: "Uptrend",
            trend_down: "Downtrend",
            stress: "Stress / deep drawdown",
        },
        equityDrawdownTitle: "Equity/Drawdown",
        equity: "Equity",
        drawdown: "Drawdown",
        equityPanel: "Equity and High-Water Mark line",
        drawdownPanel: "Drawdown, %",
        compareTitle: "Sandbox/Live comparison",
        compareCurveTitle: "Normalized equity curve (base = 100)",
        viewSingle: "Single mode",
        viewCompare: "Compare",
        helpEquity: "Equity line over time; dashed line is the High-Water Mark (historical peak).",
        helpDrawdown: "Drawdown from local equity peak, in percent. Higher line means deeper drawdown.",
        helpEquityCompare: "Compares equity trajectories of sandbox and live on the same timeline.",
        helpDrawdownCompare: "Compares drawdown depth between sandbox and live to assess execution stability.",
        helpCompareCurve: "Normalized comparison (start = 100) shows relative dynamics without different initial capital bias.",
        exportCsv: "Export CSV",
        exporting: "Exporting…",
        exportDone: "Report exported: {{file}}",
        exportS3: "Export to S3",
        exportingS3: "Uploading to S3…",
        exportS3Done: "Report in S3: {{file}}",
        exportS3Disabled: "S3 export is not configured on the server",
    },
    pro: {
        riskCockpitTitle: "Risk cockpit",
        riskCockpitHint: "Daily limit usage and exposure by ticker.",
        openExecutionJournal: "Open execution journal",
        unrealizedPnl: "Unrealized PnL",
        realizedPnlToday: "Realized PnL (today)",
        dailyLossHeadroom: "Daily loss headroom",
        notionalHeadroom: "Notional headroom",
        exposureNotional: "Exposure, notional",
        pctOfMax: "% of max",
        noExposureRows: "No per-ticker rows.",
        executionQualityTitle: "Execution quality",
        executionQualityHint: "Order lifecycle and slippage metrics (mock).",
        expectedSlippageBps: "Expected slippage, bps",
        realizedSlippageBps: "Realized slippage, bps",
        avgSlippageBps: "Avg. slippage, bps",
        baselineRealizedSlippageBps: "Baseline realized slippage, bps",
        slippageImprovementKpi: "Improvement vs baseline (actual / target)",
        shadowVsLiveSlippage: "Shadow vs live (slippage)",
        modeSwitchNoDowntimeKpi: "Mode switch without downtime",
        shadowLiveDivergenceMeasuredKpi: "Shadow/live divergence measured",
        partialFillRate: "Partial fill rate",
        avgOrderLifetime: "Avg. order lifetime, s",
        avgLatencyMs: "Average execution latency, ms",
        avgFeeTaxRateBps: "Fees and taxes, bps",
        marketLimitShare: "Market / limit share",
        qualitySampleSize: "Sample size",
        liveKillTitle: "Enable kill switch in live mode?",
        liveKillDescription: "This will stop new signals, cancel or block working orders per policy, and halt automated strategies for live until you disable kill switch. Continue?",
        confirmDanger: "Confirm",
        notificationsTitle: "Notifications & session",
        notificationsHint: "Choose which events should trigger email alerts (simulated in mock; nothing is sent).",
        notifyKillSwitch: "Email on kill switch",
        notifyApiDown: "Email on API / connectivity issues",
        notifyCriticalReason: "Email on critical reason codes from risk / execution",
        scheduleTitle: "Session window",
        sessionStart: "Session start (local)",
        sessionEnd: "Session end (local)",
        timezone: "Timezone (IANA)",
        onlyRth: "Regular trading hours only",
        fieldHint: {
            sessionStart: "Local start of the agent window (HH:mm) in the selected timezone. Used in tradability checks together with T-Invest status.",
            sessionEnd: "Local end of the agent window. Outside it, new orders may be blocked even if the exchange looks open.",
            timezone: "Agent window timezone (e.g. Asia/Yekaterinburg). Does not replace StrategyPolicy window in Europe/Moscow.",
            onlyRth: "Restrict the agent window to regular-session rules. Affects MA frequency less than Risk limits and strategy params.",
            lotMin: "Minimum number of lots allowed per order.",
            lotMax: "Maximum number of lots allowed per order.",
            lotStep: "Step (increment) for lot values.",
        },
        prefsMockNote: "In mock mode preferences are kept in memory for this session only.",
        savePrefs: "Save preferences",
        liveStrategyConfirmActivateTitle: "Activate strategy in live mode?",
        liveStrategyConfirmActivateDescription: "A live active strategy can place real orders. Make sure risk limits and policies are correct. Continue?",
        liveStrategyConfirmDeactivateTitle: "Deactivate strategy in live mode?",
        liveStrategyConfirmDeactivateDescription: "The strategy will stop taking new signals; working orders may still need manual handling per policy. Continue?",
        liveCancelAllTitle: "Cancel all open orders in live mode?",
        liveCancelAllDescription: "This mass-cancel applies to orders matching the current filters. Confirm to proceed.",
        lotSettingsTitle: "Lot settings",
        lotMin: "Min lots",
        lotMax: "Max lots",
        lotStep: "Lot step",
    },
    activation: {
        title: "Account not activated for trading",
        lead: "You are signed in as {{email}}. Trading tools stay locked until an administrator approves your account.",
        hintPending: "Please wait for activation. You can check again after the administrator has processed your request.",
        hintSuspended: "Your access was suspended. Contact support or your administrator for details.",
        apiNote: "In live mode, trading APIs return an error for inactive accounts; the UI will show this screen.",
        refreshStatus: "Check status",
    },
    admin: {
        title: "Users",
        navLabel: "Administration sections",
        nav: {
            users: "Users",
            limits: "T-Invest limits",
        },
        limits: {
            title: "T-Invest API limits",
            loading: "Loading analysis…",
            summary: "Summary",
            overall: "Status",
            worstRatio: "Load (worst ratio)",
            tariffToken: "Tariff token",
            watchlist: "Watchlist tickers",
            warmupReady: "ready",
            warmupPending: "warming up",
            warmupStep: "Warmup step",
            warmupTitle: "Cache warmup (FIGI + trading status)",
            warmupDisabled: "MARKET_WARMUP_ENABLED=false — autotrading gate is off.",
            warmupAutoRefresh: "Refreshing every 10s",
            warmupInProgress: "Warming up",
            warmupModeDisabled: "Broker disabled for mode",
            warmupFigi: "FIGI (FindInstrument)",
            warmupStatus: "Trading status",
            warmupNoTickers: "No watchlist tickers — ready without warmup.",
            warmupLastStep: "Last worker step",
            warmupUpdated: "Updated",
            warmupReadyAt: "Ready since",
            warmupSkipped: "Skipped",
            warmupHint: "Until ready, strategy workers skip auto-orders. Speed up: make warmup-market-cache or «Warmup step». Dev: MARKET_WARMUP_OVERRIDE=true.",
            modeBlock: "Mode {{mode}}",
            colMethod: "Method / pool",
            colUsed: "Req/min",
            colLimit: "Limit",
            colRatio: "Share",
            colStatus: "Status",
            metrics: "API counters (24h)",
            venueRouting: "Venue routing (24h)",
        },
        filters: "Filters",
        search: "Search",
        searchPlaceholder: "Email, name, id",
        statusFilter: "Status",
        statusAll: "All",
        statusPending: "Pending activation",
        statusActive: "Active",
        statusSuspended: "Suspended",
        applyFilters: "Apply",
        loading: "Loading users…",
        empty: "No users match the filters.",
        tableLabel: "User accounts",
        colEmail: "Email",
        colId: "ID",
        copyId: "Copy ID",
        copyIdFailed: "Could not copy ID",
        colRegistered: "Registered",
        colStatus: "Status",
        colRoles: "Roles",
        colSandbox: "Sandbox",
        sandboxOn: "On",
        sandboxOff: "Off",
        sandboxHint: "Without sandbox the user is live-only; sandbox MD is not requested",
        colNote: "Moderator note",
        colActions: "Actions",
        actionActivate: "Activate",
        actionSuspend: "Suspend",
        actionRevoke: "Revoke to pending",
        actionSaveRoles: "Save roles",
        selfRoleHint: "You cannot remove admin from your own account",
        role: {
            trader: "Trader",
            admin: "Administrator",
            auditor: "Auditor",
        },
        pagination: "Rows {{from}}–{{to}} of {{total}}",
        page: "Page {{page}} of {{pageCount}}",
        prev: "Previous",
        next: "Next",
        activation: {
            pending_activation: "Pending",
            active: "Active",
            suspended: "Suspended",
        },
    },
    pnlReport: {
        title: "Positions & PnL",
        subtitle: "Open ITR positions and ticker PnL (ITR / broker / reconciliation) in one card list. Manual broker trades are highlighted.",
        period: { all: "All time", today: "Today", custom: "Custom" },
        periodAll: "All time (ITR trades)",
        periodToday: "Current day (UTC)",
        applyPeriod: "Apply",
        brokerUnavailable: "Broker data unavailable — connect a token and sync portfolio. ITR data only.",
        itrRealized: "ITR realized",
        brokerRealized: "Broker realized",
        diff: "Diff (ITR − broker)",
        itrUnrealized: "ITR unrealized",
        byTicker: "Positions & tickers",
        cardsHint: "Open positions first, then tickers with PnL history only. Each card: position + ITR/broker reconciliation.",
        openPositionsCount: "Open: {{count}}",
        cardHeroOpen: "Unrealized",
        cardHeroRealized: "ITR realized",
        cardOpenQty: "Open {{qty}}",
        cardShortQty: "short {{qty}}",
        cardClosed: "No position",
        cardStrategies: "Strategies: {{count}}",
        cardSells: "Sells ITR / broker",
        cardNoPnlRow: "No PnL row for the period — open position only.",
        empty: "No positions or trades in the selected period.",
        colTicker: "Ticker",
        colItrRealized: "ITR real.",
        colBrokerRealized: "Broker real.",
        colDiff: "Δ",
        colItrSells: "ITR sells",
        colBrokerSells: "Broker sells",
        colManual: "Manual",
        colItrUnrealized: "ITR unreal.",
        colBrokerUnrealized: "Broker yield",
        colDate: "Date",
        colSide: "Side",
        colQty: "Qty",
        colPayment: "Payment",
        colCommission: "Commission",
        colOrderId: "Order ID",
        manualBadge: "manual",
        manualCounts: "buys {{buys}}, sells {{sells}}",
        manualTitle: "Manual broker operations",
        manualHint: "Broker ops not linked to an ITR order (ticker, side, size, time, price). Broker API omits orderId — matching uses our DB trades.",
        manualEmpty: "No manual operations in the period.",
        noOrderId: "no ID",
        brokerSyncedAt: "Broker portfolio snapshot: {{at}}",
    },
    journal: {
        title: "Execution journal",
        subtitle: "Trace by causal tags: signal → risk gate → router → venue; filter by tag to find bottlenecks.",
        tabExecution: "Execution",
        tabSkips: "Silent skips",
        skipsHint: "Signals that never became an order or were rejected at the broker before save. Repeats are aggregated (hit_count).",
        skipsEmpty: "No silent skip entries for the current filters.",
        skipsMeta: "Source: {{source}} · repeats: {{hits}} · first: {{first}}",
        skipScheduleTitle: "Exchange schedule",
        skipMainWindow: "Main session",
        skipEveningWindow: "Evening session",
        skipTradingStatus: "Instrument status",
        skipTradabilitySource: "Status source",
        skipBlockedReasons: "Block reasons",
        skipBrokerMessage: "Broker message",
        skipScheduleSource: {
            tinvest: "T-Invest API",
            cache: "Redis cache",
            fallback: "fallback (default)",
        },
        filters: "Filters",
        causalFilterHint: "Quick filter: show traces that include at least one step with this tag.",
        causalFilterAll: "All tags",
        filterStrategy: "Strategy ID",
        filterTicker: "Ticker",
        dateFrom: "From",
        dateTo: "To",
        applyFilters: "Apply",
        exportCsv: "Export CSV",
        loading: "Loading journal…",
        empty: "No entries for the current filters.",
        shadowVsLiveTitle: "Shadow vs live comparison",
        shadowVsLiveSummary: "Shadow: {{shadow}}, live: {{live}}, total executions: {{total}}.",
        shadowBadge: "SHADOW",
        stage: {
            signal: "Signal",
            risk_gate: "Risk gate",
            router: "Router",
            venue: "Venue",
        },
        stepStatus: {
            ok: "OK",
            pending: "Pending",
            rejected: "Rejected",
        },
    },
    portfolioComparison: {
        title: "Portfolio: broker vs ITR",
        hint: "Left — T-Invest account snapshot (GetPortfolio/GetPositions). Right — positions and PnL from ITR Agent orders only.",
        refresh: "Refresh",
        refreshing: "Refreshing…",
        sync: "Sync from T-Invest",
        syncing: "Syncing…",
        syncInProgress: "Fetching portfolio from broker…",
        syncStepToken: "Validate token and resolve account",
        syncStepPortfolio: "OperationsService/GetPortfolio — portfolio value",
        syncStepPositions: "OperationsService/GetPositions — positions and cash",
        syncStepSave: "Save snapshot to broker_portfolio_snapshots",
        syncSuccess: "Broker snapshot updated",
        importTrades: "Import broker trades",
        importingTrades: "Importing…",
        importTradesHint: "Unmatched BUY/SELL from T-Invest (30 days) → filled ITR orders and position updates.",
        importConfirmTitle: "Import broker trades?",
        importConfirmDescription: "Synthetic filled orders will be created for broker operations missing in ITR (strategy “broker / manual”). Already linked trades are skipped. Uses the current mode (sandbox/live).",
        importSuccess: "Import: +{{imported}} (scanned {{scanned}}, matched {{matched}}, already {{already}}).",
        reconcileTrades: "Re-sync broker trades",
        reconcilingTrades: "Re-syncing…",
        reconcileTradesHint: "Refresh fee, fill price and metadata on already-imported orders from T-Invest ops (90 days, with trades).",
        reconcileConfirmTitle: "Re-sync broker trades?",
        reconcileConfirmDescription: "Existing ITR orders (including prior imports) will be reconciled with broker operations: commission, fill price, broker_trade_id. Positions are not rebuilt — use align if qty still drifts.",
        reconcileSuccess: "Reconcile: updated {{reconciled}}, unchanged {{unchanged}}, new imports {{imported}} (scanned {{scanned}}).",
        alignPositions: "Align positions to broker",
        aligningPositions: "Aligning…",
        alignPositionsHint: "Emergency: overwrite ITR qty/avg from the broker snapshot. Does not rebuild order history.",
        alignConfirmTitle: "Align positions to broker?",
        alignConfirmDescription: "ITR positions will be forced to the T-Invest snapshot (create/update/delete). Prefer importing trades first. Local qty/avg changes are not reversible.",
        alignSuccess: "Align: updated {{updated}}, created {{created}}, removed {{removed}}.",
        loading: "Loading portfolio…",
        syncedAt: "updated {{when}}",
        brokerTitle: "Broker (T-Invest)",
        itrTitle: "ITR (our orders)",
        total: "Portfolio value",
        available: "Available cash",
        expectedYield: "Expected yield",
        exposure: "Exposure",
        unrealized: "Unrealized PnL",
        noPositions: "No open positions",
        brokerUnavailable: "No broker snapshot yet. Click “Sync from broker”.",
        colTicker: "Ticker",
        colQty: "Qty",
        colPrice: "Price",
        colValue: "Value",
        colPnl: "PnL",
        syncStatus: {
            ok: "Sync is up to date",
            stale: "Snapshot is stale — run sync",
            unavailable: "Broker data unavailable",
        },
    },
    tradingPnl: {
        title: "Trading result",
        hint: "Filled trades and open positions only. Deposits and withdrawals are excluded.",
        modeHint: "ITR Agent internal model — not the broker account.",
        realizedSection: "Locked in by trades",
        realizedHint: "Changes only when orders are filled. Stays flat when trading is stopped by limits.",
        unrealizedSection: "Open positions (mark-to-market)",
        unrealizedHint: "Based on live market price; updates with quotes even without new trades.",
        realizedTotal: "Realized (all time)",
        today: "Realized today",
        unrealized: "Unrealized P&L",
        combinedFootnote: "Combined view (realized + unrealized): {{value}} — moves with the market.",
    },
    limits: {
        exhausted: "Limit exhausted",
        warning: "Limit almost exhausted",
        dailyNotional: "Daily notional",
        dailyTrades: "Trades today",
        dailyLoss: "Daily loss",
    },
    strategyGroups: {
        title: "Strategy Groups",
        namePlaceholder: "Group name",
        nameRequired: "Please enter a group name.",
        create: "New Group",
        createSection: "Create Group",
        createOpen: "Create Group",
        createCollapse: "Collapse",
        statusCreated: "Group created",
        statusStrategyMoved: "Strategy moved",
        ungrouped: "Ungrouped",
        deleteConfirm: "Delete group?",
        deleteDesc: "All strategies will be moved to ungrouped.",
        delete: "Delete Group",
        newGroup: "New group",
        groupCreated: "Group created",
        groupUpdated: "Group updated",
        groupRemoved: "Group removed",
        groupRequired: "Enter group name",
        groupRename: "Rename",
        groupDelete: "Delete group",
        groupDeleteConfirm: "Delete group and move all strategies to 'Ungrouped'?",
        groupCreated: "Group created",
        groupUpdated: "Group updated",
        groupRemoved: "Group removed",
        moveStrategy: "Move to group",
    },
    strategySearch: {
        placeholder: "Search by ticker or name…",
        noResults: "No strategies found",
        resultsCount: "Found: {{count}}",
    },
};
